import {
  Modal,
  Button,
  Steps,
  Table,
  Tag,
  Select,
  Form,
  Checkbox,
  List,
  Alert,
  message,
  Spin,
  DatePicker,
  Flex,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import { getTagColor } from '../../../../core/utils/commonUtils';
import Scenario from '../../../../domain/models/Scenario';
import SubscriptionsContext from '../../../../presentation/contexts/data/SubscriptionsContext';
import DistributionType, {
  distributionTypeAsString,
} from '../../../../domain/enums/@shared/DistributionType';
import ScenariosContext from '../../../../presentation/contexts/data/ScenariosContext';
import ContactsContext from '../../../../presentation/contexts/data/ContactsContext';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';
import dayjs, { Dayjs } from 'dayjs';
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { FormContainer } from '@leedpulse-monorepo/components';
import { IContact } from '@leedpulse-monorepo/interfaces';
import { BookOutlined, CalendarOutlined, ClockCircleOutlined, CloseCircleOutlined, ContactsOutlined, InfoCircleOutlined, PhoneOutlined, ReloadOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';

interface ContactActionModalProps {
  selectedContacts: IContact[];
  scenarios: Scenario[];
  visible: boolean;
  subscriptionId: string;
  onClose?: () => void;
  onUpdated?: () => void;
}

const ContactActionModal: React.FC<ContactActionModalProps> = ({
  selectedContacts,
  scenarios,
  visible,
  subscriptionId,
  onClose,
  onUpdated,
}) => {
  const [current, setCurrent] = useState(0);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedDistribution, setSelectedDistribution] = useState<DistributionType | null>(DistributionType.none);
  const [selectedStartDistributionDate, setSelectedStartDistributionDate] = useState(dayjs() as Dayjs | null);
  const [actionCanceled, setActionCanceled] = useState(false);
  const [actionProgrammed, setActionProgrammed] = useState(false);

  const contactsContext = useContext(ContactsContext);
  const subscriptionsContext = useContext(SubscriptionsContext);
  const scenariosContext = useContext(ScenariosContext);
  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    subscriptionsContext.fetchAndSelectById(subscriptionId);
  }, []);

  useEffect(() => {
    if (subscriptionsContext.selected) {
      subscriptionsContext.fetchUsers(subscriptionId);
      scenariosContext.fetchByIds(subscriptionsContext.selected.idScenarios);
    }
  }, [subscriptionsContext.selected]);

  const isInformationEmpty = () => {
    return (
      !selectedScenario &&
      !selectedOwner &&
      !selectedDistribution &&
      !actionCanceled &&
      !actionProgrammed
    );
  };

  const steps = [
    {
      title: 'Sélection',
      content: (
        <div>
          <h3><Flex align="center"><UserOutlined style={{ marginRight: 6 }} /> Contacts sélectionnés ({selectedContacts.length})</Flex></h3>
          <Table
            pagination={false}
            scroll={{ y: 60 * 5 }}
            columns={[
              {
                title: <Flex align="center"><UserOutlined style={{ marginRight: 6 }} /> Nom complet</Flex>,
                render: (contact: IContact) => {
                  return (
                    <span>
                      {contact.contact.firstname} {contact.contact.lastname}
                    </span>
                  );
                },
              },
              {
                title: '✏️ Tags',
                render: (contact: IContact) => {
                  return (
                    <>
                      <Tag
                        className="contact-tile__content__owner-tag"
                        color={getTagColor(
                          contact?.qualification_ai?.qualification ||
                          'not_available'
                        )}
                      >
                        &#8226;{' '}
                        {getInterestLevel(
                          contact?.qualification_ai?.qualification ||
                          'not_available'
                        )}
                      </Tag>
                      <Tag
                        className="contact-tile__content__owner-tag"
                        color="lightgray"
                      >
                        {scenarios.find(
                          (scenario) =>
                            scenario.id ===
                            contact?.contact?.prospect_category
                        )?.name || contact?.contact?.prospect_category}
                      </Tag>
                    </>
                  );
                },
              },
              {
                title: <Flex align="center"><PhoneOutlined style={{ marginRight: 6 }} /> Téléphone</Flex>,
                render: (contact: IContact) => {
                  return (
                    <span>
                      {contact?.contact?.phone || 'N/A'}
                    </span>
                  );
                },
              },
            ]}
            dataSource={selectedContacts}
          ></Table>
        </div>
      ),
    },
    {
      title: 'Propriétés',
      content: (
        <div>
          <h3 style={{ marginBottom: '1rem' }}>
            <Flex align="center"><BookOutlined style={{ marginRight: 6 }} /> Propriétés des contacts à mettre à jour</Flex>
          </h3>
          <Alert
            style={{ marginBottom: '1rem' }}
            message="Sélectionnez les propriétés à mettre à jour ou sinon passez à l'étape suivante"
            type="info"
            showIcon
          />
          <FormContainer twoColumnsMax>
            <Form.Item label="🎞️ Nouveau scénario">
              <Select
                style={{ width: 400, marginBottom: '1rem' }}
                placeholder="eg. Ancien client"
                allowClear
                value={selectedScenario}
                onChange={(value) => setSelectedScenario(value)}
              >
                {scenariosContext.scenarios.map((scenario) => {
                  return (
                    <Select.Option key={scenario.id} value={scenario.id}>
                      {scenario.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item label={<Flex align="center"><TeamOutlined style={{ marginRight: 6 }} /> Nouveau conseiller attitré</Flex>}>
              <Select
                style={{ width: 400, marginBottom: '1rem' }}
                placeholder="eg. Stéphanie"
                allowClear
                value={selectedOwner}
                defaultValue={null}
                onChange={(value) => setSelectedOwner(value)}
              >
                {subscriptionsContext.subscriptionUsers.map((user) => {
                  return (
                    <Select.Option key={user.uid} value={user.uid}>
                      {user.firstname} {user.lastname}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </FormContainer>
        </div>
      ),
    },
    {
      title: 'Action',
      content: (
        <div>
          <h3 style={{ marginBottom: '1rem' }}>
            <Flex align="center"><ClockCircleOutlined style={{ marginRight: 6 }} /> Actions sur les messages planifiés</Flex>
          </h3>
          <Alert
            style={{ marginBottom: '1rem' }}
            message="Si vous ne souhaitez rien faire, passez à l'étape suivante"
            type="info"
            showIcon
          />
          <FormContainer oneColumnMax>
            <Checkbox
              checked={actionCanceled}
              onChange={(e) => setActionCanceled(e.target.checked)}
            >
              <Flex align="center"><CloseCircleOutlined style={{ marginRight: 6 }} /> Annuler les messages planifiés</Flex>
            </Checkbox>
            <Checkbox
              checked={actionProgrammed}
              onChange={(e) => setActionProgrammed(e.target.checked)}
            >
              <Flex align="center"><ReloadOutlined style={{ marginRight: 6 }} /> Reprogrammer de nouveaux messages</Flex>
            </Checkbox>
            {actionProgrammed && (
              <ul>
                <li>
                  <Form.Item label={<Flex align="center"><ClockCircleOutlined style={{ marginRight: 6 }} /> Durée de la répartition</Flex>}>
                    <Select
                      style={{ minWidth: 200, maxWidth: 400, marginBottom: '1rem' }}
                      placeholder="eg. répartition sur 1 mois"
                      allowClear
                      value={selectedDistribution}
                      onChange={(value) => setSelectedDistribution(value)}
                    >
                      {Object.keys(DistributionType).map((key) => (
                        <Select.Option
                          key={key}
                          value={
                            DistributionType[
                            key as keyof typeof DistributionType
                            ]
                          }
                        >
                          {distributionTypeAsString(
                            DistributionType[
                            key as keyof typeof DistributionType
                            ]
                          )}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </li>
                <li>
                  <Form.Item label={<Flex align="center"><CalendarOutlined style={{ marginRight: 6 }} /> Date de début de la répartition</Flex>}>
                    <DatePicker
                      style={{ width: 300, marginBottom: '1rem' }}
                      value={selectedStartDistributionDate}
                      onChange={(value) => setSelectedStartDistributionDate(value)}
                      format="DD/MM/YYYY"
                      minDate={dayjs()}
                    />
                  </Form.Item>
                </li>
              </ul>
            )}
          </FormContainer>
        </div>
      ),
    },
    {
      title: 'Récapitulatif',
      content: (
        <div>
          <h3><InfoCircleOutlined style={{ marginRight: 6 }} /> Récapitulatif</h3>
          <List
            itemLayout="horizontal"
            dataSource={[
              {
                title: 'Nombre de contacts impactés',
                updated: true,
                description: <p><ContactsOutlined style={{ marginRight: 6 }} /> {selectedContacts.length}</p>,
              },
              {
                title: 'Propriétés des contacts mises à jour',
                updated:
                  selectedScenario || selectedOwner || selectedDistribution,
                description: (
                  <ul>
                    {selectedScenario && (
                      <li>
                        <span role="img" aria-label="movie">🎞️</span> Nouveau scénario :{' '}
                        <b>
                          {
                            scenarios.find(
                              (scenario) => scenario.id === selectedScenario
                            )?.name
                          }
                        </b>
                      </li>
                    )}
                    {selectedOwner && (
                      <li>
                        <TeamOutlined style={{ marginRight: 6 }} /> Nouveau conseiller attitré :{' '}
                        <b>
                          {
                            subscriptionsContext.subscriptionUsers.find(
                              (user) => user.uid === selectedOwner
                            )?.firstname
                          }{' '}
                          {
                            subscriptionsContext.subscriptionUsers.find(
                              (user) => user.uid === selectedOwner
                            )?.lastname
                          }
                        </b>
                      </li>
                    )}
                  </ul>
                ),
              },
              {
                title: "Types d'action",
                updated: actionCanceled || actionProgrammed,
                description: (
                  <ul>
                    {actionCanceled && (
                      <li>
                        <b><CloseCircleOutlined style={{ marginRight: 6 }} /> Annuler les messages planifiés</b>
                      </li>
                    )}
                    {actionProgrammed && (
                      <li>
                        <b><ReloadOutlined style={{ marginRight: 6 }} /> Reprogrammer de nouveaux messages</b>
                        {selectedDistribution && (
                          <ul>
                            <li>
                              <ClockCircleOutlined style={{ marginRight: 6 }} /> Nouveau avec durée de la répartition :{' '}
                              <b>
                                {distributionTypeAsString(selectedDistribution)}
                              </b>
                            </li>
                          </ul>
                        )}
                        {selectedStartDistributionDate && (
                          <ul>
                            <li>
                              <CalendarOutlined style={{ marginRight: 6 }} /> Nouveau avec date de début de la répartition :{' '}
                              <b>
                                {selectedStartDistributionDate.format("DD/MM/YYYY")}
                              </b>
                            </li>
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                ),
              },
            ]}
            renderItem={(item, index) => {
              if (!item.updated) return null;
              return (
                <List.Item>
                  <List.Item.Meta
                    title={item.title}
                    description={item.description}
                  />
                </List.Item>
              );
            }}
          />
          {isInformationEmpty() && <p>Aucune information à mettre à jour</p>}
        </div>
      ),
    },
  ];

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  const handleReset = () => {
    setCurrent(0);
    setSelectedScenario(null);
    setSelectedOwner(null);
    setSelectedDistribution(null);
    setActionCanceled(false);
    setActionProgrammed(false);
    onClose?.();
  };

  const handleLaunch = () => {
    const sApiKey = subscriptionsContext.selected?.apiKey;
    if (sApiKey) {
      const contactIds = selectedContacts.map((contact) => contact.id);
      const payload = {
        apiKey: sApiKey,
        contactIds: contactIds,
        scenarioId: selectedScenario,
        owner: selectedOwner,
        actionCanceled: actionCanceled,
        actionProgrammed: actionProgrammed,
        distribution: selectedDistribution,
        startDistributionDate: selectedStartDistributionDate,
      };
      contactsContext
        .launchActionGroup(authContext.authentication, payload)
        .then((result) => {
          if (result.status === ContextStatus.SUCCESS) {
            message.success(
              selectedContacts.length === 1
                ? '1 contact mis à jour avec succès'
                : selectedContacts.length + ' contacts mis à jour avec succès'
            );
          } else if (result.status === ContextStatus.ERROR) {
            message.error(result.errorMessage || contactsContext.errorMessage);
          }
          handleReset();
          onUpdated?.();
        })
        .catch((error) => {
          message.error(error.message);
        });
    }
  };

  return (
    <Modal
      title="Lancer une action"
      centered
      open={visible}
      styles={{
        body: { height: 500 },
      }}
      footer={
        <div>
          {current > 0 && (
            <Button
              style={{ margin: '0 8px' }}
              disabled={
                contactsContext.launchActionStatus === ContextStatus.LOADING
              }
              onClick={() => prev()}
            >
              Précédent
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button type="primary" onClick={() => next()}>
              Suivant
            </Button>
          )}
          {current === steps.length - 1 && !isInformationEmpty() && (
            <Button
              type="primary"
              loading={
                contactsContext.launchActionStatus === ContextStatus.LOADING
              }
              onClick={() => handleLaunch()}
            >
              Lancer
            </Button>
          )}
          {current === steps.length - 1 && isInformationEmpty() && (
            <Button type="primary" onClick={() => handleReset()}>
              Terminer
            </Button>
          )}
        </div>
      }
      width={1000}
      onCancel={() => {
        handleReset();
      }}
    >
      <Steps
        progressDot
        style={{ marginTop: '2rem', marginBottom: '2rem' }}
        current={current}
        size="small"
        items={items}
      />
      {contactsContext.launchActionStatus === ContextStatus.LOADING ? (
        <div style={{ marginTop: '12rem', textAlign: 'center' }}>
          <Spin style={{ marginBottom: '2rem' }} />
          <h3>Mise à jour des contacts en cours</h3>
        </div>
      ) : (
        <div>{steps[current].content}</div>
      )}
    </Modal>
  );
};

export default ContactActionModal;
